import { Component, ChangeDetectionStrategy, ElementRef, OnInit, AfterViewInit, viewChild, signal, input, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TooltipModule } from 'primeng/tooltip';
import { getYear } from 'date-fns';
import { UiService } from 'src/app/core/services/ui.service';

/**
 * Shared visual shell for public auth pages (Login, Forgot Password, ...).
 * Renders the split layout (form panel + video welcome panel), brand header,
 * contact info, and footer/social links. The page-specific form body is
 * projected via <ng-content>.
 *
 * Named AuthPageShellComponent (not AuthLayoutComponent) to avoid colliding
 * with the pre-existing `app-auth-layout` / AuthLayoutComponent in
 * src/app/layouts/auth-layout/, which is an unrelated router-outlet wrapper
 * used at the top of the /auth route tree.
 *
 * This component was extracted from login.component.html/.scss verbatim —
 * login.component itself was NOT modified when this was introduced, to avoid
 * any regression risk on the existing, already-verified login page. A future,
 * separate change may migrate LoginComponent to consume this shell directly.
 */
@Component({
  selector: 'app-auth-page-shell',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TooltipModule],
  templateUrl: './auth-layout.component.html',
  styleUrls: ['./auth-layout.component.scss'],
  host: {
    // Classic pins the auth pages to the dark theme; the new UI follows the app theme.
    '[attr.data-theme]': 'ui.isNew() ? null : "dark"',
  },
})
export class AuthPageShellComponent implements OnInit, AfterViewInit {
  /** Small line above the main title, e.g. "Welcome!" */
  readonly subtitle = input<string>('Welcome!');
  /** Main heading, e.g. "Sign Into Your Account" */
  readonly title = input<string>('');

  readonly videoPlayer = viewChild<ElementRef<HTMLVideoElement>>('videoPlayer');

  protected readonly ui = inject(UiService);

  protected presentYear = signal<string>('');

  ngOnInit(): void {
    this.presentYear.set(getYear(new Date()).toString());
  }

  ngAfterViewInit(): void {
    const video = this.videoPlayer()?.nativeElement;
    if (video) {
      video.muted = true;
      video.play().catch(() => {});
    }
  }
}
